import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { completeOnboarding, skipOnboarding } from '../../services/onboardingService';
import api from '../../services/api';
import WelcomeStep from './steps/WelcomeStep';
import ConnectSlackStep from './steps/ConnectSlackStep';
import SlackAutoImportStep from './steps/SlackAutoImportStep';
import CompleteStep from './steps/CompleteStep';

const OnboardingWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [slackConnected, setSlackConnected] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { setCurrentUser } = useAppContext();

  const totalSteps = 4;

  // Detect OAuth callback and restore wizard step
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('slack_connected') === 'true') {
      setCurrentStep(3);
      setSlackConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSkip = async () => {
    try {
      setError(null);
      await skipOnboarding();
      setCurrentUser(prev => ({ ...prev, onboarding_completed: true }));
      navigate('/home');
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      setError('Failed to skip onboarding. Please try again.');
    }
  };

  const handleFinish = async () => {
    try {
      setError(null);
      await completeOnboarding();
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
      navigate('/home');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setError('Failed to complete onboarding. Please try again.');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep onNext={handleNext} onSkip={handleSkip} />;
      case 2:
        return (
          <ConnectSlackStep
            onNext={() => {
              setSlackConnected(true);
              handleNext();
            }}
            onSkip={() => setCurrentStep(4)}
            onBack={handleBack}
          />
        );
      case 3:
        if (slackConnected) {
          return (
            <SlackAutoImportStep
              onSkipToComplete={() => setCurrentStep(4)}
              onBack={handleBack}
            />
          );
        }
        // If not connected (shouldn't happen), go to complete
        setCurrentStep(4);
        return null;
      case 4:
        return <CompleteStep onFinish={handleFinish} onBack={handleBack} />;
      default:
        return <WelcomeStep onNext={handleNext} onSkip={handleSkip} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm text-gray-600">
              {Math.round((currentStep / totalSteps) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="py-8">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
